/**
 * Consume a stashed audit-report claim after sign-up (2026-08-19 teardown).
 *
 * Mounted at App level next to useFreeScanClaim and for the same reason: a
 * brand-new signup is redirected into onboarding, which would unmount a
 * Layout-scoped hook mid-claim. Once bootstrap has produced an activeTeamId,
 * it POSTs /api/site-audits/:shareId/claim, which creates a project holding
 * the report's violations as issues — the findings the CTA promised now
 * exist in the account the click created.
 *
 * Fires once per signed-in session (firedRef). A terminal outcome clears the
 * stash; a transient failure leaves it so the next app load retries, bounded
 * by the 1h TTL in siteAuditClaim.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppSession } from './useAppSession';
import { useApiMutation } from './useApiMutation';
import { siteAuditsApi, type ClaimSiteAuditResult } from '../lib/api/siteAudits';
import { ApiError } from '../lib/api/apiError';
import { readSiteAuditClaim, clearSiteAuditClaim } from '../lib/siteAuditClaim';
import { log } from '@/lib/logger';

interface ClaimVars {
  shareId: string;
}

export function useSiteAuditClaim(): void {
  const { data: session } = useAppSession();
  const queryClient = useQueryClient();
  const firedRef = useRef(false);

  const claim = useApiMutation<ClaimSiteAuditResult, ClaimVars>({
    mutationFn: (token, { shareId }) => siteAuditsApi.claim(token, shareId),
  });
  const claimMutate = claim.mutate;

  const teamId = session?.activeTeamId;

  useEffect(() => {
    if (firedRef.current) return;
    if (!teamId) return; // wait until bootstrap created the account + team

    const stash = readSiteAuditClaim();
    if (!stash) return; // nothing to claim (absent or expired)

    firedRef.current = true;
    log.info('[useSiteAuditClaim] claiming audit report', { shareId: stash.shareId, teamId });

    claimMutate(
      { shareId: stash.shareId },
      {
        onSuccess: (result) => {
          log.info('[useSiteAuditClaim] claim succeeded', {
            projectId: result.projectId,
            issueCount: result.issueCount,
            alreadyClaimed: result.alreadyClaimed,
          });
          clearSiteAuditClaim();
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          queryClient.invalidateQueries({ queryKey: ['app-session'] });
          if (!result.alreadyClaimed) {
            toast.success(
              result.issueCount === 1
                ? 'Your audit finding is in your account, ready for your team.'
                : `Your ${result.issueCount} audit findings are in your account, ready for your team.`,
            );
          }
        },
        onError: (err) => {
          const status = err instanceof ApiError ? err.status : undefined;
          const code = err instanceof ApiError ? err.code : undefined;
          log.warn('[useSiteAuditClaim] claim failed', { status, code, message: err.message });

          const terminal = status === 404 || status === 422 || code === 'EMPTY_REPORT';
          if (terminal) {
            clearSiteAuditClaim();
            toast.error('We could not import that report. Your account is ready; scan the site to rebuild it.');
          } else {
            // Transient (network / 5xx / team still settling, incl. the 403
            // no-team window during onboarding): leave the stash so the next
            // app load retries, and re-arm this session's guard.
            firedRef.current = false;
          }
        },
      },
    );
  }, [teamId, claimMutate, queryClient]);
}
